import { useSidePanelDispatch } from '@/components/sidepanel/side-panel-provider';
import { PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useTranslation } from '@/hooks/use-translation';
import { Outlet, createFileRoute, useMatches } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

export const Route = createFileRoute('/_app/settings')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('desktop_ui');
  const matches = useMatches();
  const setSidePanelState = useSidePanelDispatch();

  // Only the dynamic /numbers/$number route brings its own NumberSettingsHeader
  // chrome — the /numbers index uses the default settings shell.
  const isNumberDetailPage = useMemo(() => {
    return matches.some((match) => match.routeId === '/_app/settings/numbers/$number');
  }, [matches]);

  // Any route under /settings/integrations/* renders its own PageHeader
  // (dynamic `$integrationName` + dedicated static routes like
  // `claude-connector`). The integrations index keeps the default shell.
  const isIntegrationDetailPage = matches.some(
    (match) =>
      match.routeId.startsWith('/_app/settings/integrations/') &&
      match.routeId !== '/_app/settings/integrations/',
  );

  // Map route IDs to their translation keys
  const pageTitle = useMemo(() => {
    const currentRoute = matches[matches.length - 1];
    const routeId = currentRoute?.routeId;

    const routeTitleMap: Record<string, string> = {
      '/_app/settings/profile': t('settings.sidebar.profile'),
      '/_app/settings/billing/': t('settings.sidebar.billing'),
      '/_app/settings/usage': t('settings.sidebar.usage'),
      '/_app/settings/integrations/': t('settings.sidebar.integrations'),
      '/_app/settings/integrations/$integrationName': t('settings.sidebar.integrations'),
      '/_app/settings/api': t('settings.sidebar.api'),
      '/_app/settings/webhooks': t('settings.sidebar.webhooks'),
      '/_app/settings/numbers/': t('settings.sidebar.numbers'),
      '/_app/settings/numbers/$number': t('settings.sidebar.numbers'),
      '/_app/settings/team': t('settings.sidebar.members'),
      '/_app/settings/compliance': t('settings.sidebar.compliance'),
      '/_app/settings/compliance/': t('settings.sidebar.compliance'),
      '/_app/settings/compliance/brand': t('settings.sidebar.compliance'),
      '/_app/settings/compliance/campaign': t('settings.sidebar.compliance'),
      '/_app/settings/call-tags/': t('settings.sidebar.callTags'),
      '/_app/settings/templates': t('settings.sidebar.templates'),
    };

    return routeTitleMap[routeId ?? ''] ?? t('components.sidebar.menu.settings');
  }, [matches, t]);

  useEffect(() => {
    setSidePanelState({
      open: false,
      context: {
        contactId: null,
      },
    });
  }, []);

  const content = (
    <div className="flex flex-col w-full overflow-hidden min-h-0 h-full">
      <SidebarProvider
        name="settings-sidebar"
        style={
          {
            '--sidebar-width': '280px',
          } as React.CSSProperties
        }
        className="overflow-hidden h-full"
      >
        <SidebarInset className="bg-transparent">
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </div>
  );

  if (isNumberDetailPage || isIntegrationDetailPage) {
    return content;
  }

  return (
    <PageLayout>
      <PageHeader title={pageTitle} />
      <PageContent>{content}</PageContent>
    </PageLayout>
  );
}
